import React, { useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button, Paper, TextField, Checkbox, FormControlLabel, Select, MenuItem,
  IconButton, CircularProgress, Alert,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { korteDatum } from '../../utils/postproductiePlanning';
import { SOORTEN, naarRegels, voorstelUitRegels } from '../../utils/klantplanning';

// "Planning van de klant gebruiken" in de postproductiewizard (wiki b30c9aec):
// mail plakken, screenshot plakken/kiezen of een document kiezen → de engine
// leest de mijlpalen → jij ziet per regel het voorstel en past aan → pas bij
// "Voorstel overnemen" verandert er iets in de wizard. Nooit stil overschrijven.

const ACCEPT = 'image/*,.pdf,.docx,.eml,.txt,.md';
const DOC_FASE = 'productie';

async function foutUit(r) {
  const j = await r.json().catch(() => ({}));
  return j.error || j.detail || `HTTP ${r.status}`;
}

function KlantplanningImport({
  projectId, projectNummer, deliverableNaam, huidig, onToepassen,
}) {
  const [open, setOpen] = useState(false);
  const [tekst, setTekst] = useState('');
  const [bestand, setBestand] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);
  const [resultaat, setResultaat] = useState(null); // { samenvatting, regels }
  const [bewaren, setBewaren] = useState(true);
  const [overgenomen, setOvergenomen] = useState(null); // tekst voor de rustige regel na overnemen
  const invoer = useRef(null);

  const regels = useMemo(() => resultaat?.regels || [], [resultaat]);
  const voorstel = useMemo(() => voorstelUitRegels(regels), [regels]);

  const reset = () => {
    setTekst(''); setBestand(null); setResultaat(null); setFout(null); setBezig(false);
  };

  // Screenshot uit het klembord in het tekstveld plakken = als bestand gebruiken.
  const opPlakken = (e) => {
    const beeld = Array.from(e.clipboardData?.files || []).find((f) => f.type.startsWith('image/'));
    if (beeld) {
      e.preventDefault();
      setBestand(new File([beeld], beeld.name && beeld.name !== 'image.png' ? beeld.name : 'screenshot-klantplanning.png', { type: beeld.type }));
    }
  };

  const lees = async () => {
    if (!projectId || (!tekst.trim() && !bestand)) return;
    setBezig(true); setFout(null); setResultaat(null);
    try {
      const fd = new FormData();
      if (tekst.trim()) fd.append('tekst', tekst.trim());
      if (bestand) fd.append('file', bestand);
      if (deliverableNaam) fd.append('deliverable', deliverableNaam);
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectId}/klantplanning`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error(await foutUit(r));
      const data = await r.json();
      setResultaat({ samenvatting: data.samenvatting || '', regels: naarRegels(data.mijlpalen, deliverableNaam) });
    } catch (e) {
      setFout(e.message);
    } finally {
      setBezig(false);
    }
  };

  const wijzigRegel = (id, veld, waarde) => setResultaat((r) => ({
    ...r,
    regels: r.regels.map((x) => (x.id === id
      ? { ...x, [veld]: waarde, ...(veld === 'datum' ? { zeker: true, aan: !!waarde || x.aan } : {}) }
      : x)),
  }));

  // De ruwe klantplanning bij het project bewaren (bestaand documenten-mechanisme),
  // zodat hij later terug te vinden is. Een mislukte upload blokkeert niets.
  const bewaarBijProject = async () => {
    const stukken = [];
    if (bestand) stukken.push(bestand);
    if (tekst.trim()) stukken.push(new File([tekst.trim()], 'klantplanning-mail.txt', { type: 'text/plain' }));
    let gelukt = 0;
    for (const stuk of stukken) {
      const fd = new FormData();
      fd.append('file', stuk);
      fd.append('fase', DOC_FASE);
      fd.append('naam', `Klantplanning — ${deliverableNaam || stuk.name}`);
      if (projectNummer) fd.append('projectNumber', projectNummer);
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectId}/documents`, { method: 'POST', body: fd }); // eslint-disable-line no-await-in-loop
        if (r.ok) gelukt += 1;
      } catch { /* melden hieronder */ }
    }
    return { gelukt, totaal: stukken.length };
  };

  const neemOver = async () => {
    onToepassen(voorstel);
    let regel = voorstel.notitie
      ? 'Klantplanning overgenomen; de mijlpalen staan bij de opmerkingen voor de planner'
      : 'Klantplanning overgenomen';
    if (bewaren && projectId) {
      const { gelukt, totaal } = await bewaarBijProject();
      if (totaal) {
        regel += gelukt === totaal
          ? ' · bewaard bij het project (documenten, Productie)'
          : ' · bewaren bij het project is mislukt';
      }
    }
    setOvergenomen(regel);
    setOpen(false);
    reset();
  };

  // Wat er in de wizard zou veranderen, alleen de echte wijzigingen.
  const wijzigingen = [];
  if (voorstel.startDate && voorstel.startDate !== huidig.startDate) {
    wijzigingen.push(`start ${korteDatum(voorstel.startDate)}`);
  }
  if (voorstel.endDate && voorstel.endDate !== huidig.endDate) {
    wijzigingen.push(`deadline ${korteDatum(voorstel.endDate)}`);
  }
  if (voorstel.feedbackDays && voorstel.feedbackDays !== Number(huidig.feedbackDays)) {
    wijzigingen.push(`${voorstel.feedbackDays} feedbackdag${voorstel.feedbackDays > 1 ? 'en' : ''}`);
  }
  if (voorstel.notitie) wijzigingen.push('mijlpalen in de notitie voor de planner');

  if (!open) {
    return (
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" startIcon={<AutoAwesomeIcon />}
          onClick={() => { setOpen(true); setOvergenomen(null); }}
          sx={{ textTransform: 'none', color: '#334155', borderColor: '#cbd5e1' }}>
          Planning van de klant gebruiken
        </Button>
        <Typography variant="caption" color="text.secondary">
          {overgenomen || 'mail, screenshot of document — je ziet eerst een voorstel'}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
          Planning van de klant
        </Typography>
        <IconButton size="small" aria-label="Sluiten" onClick={() => { setOpen(false); reset(); }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {!resultaat && (
        <>
          <TextField
            multiline minRows={4} maxRows={12} fullWidth size="small"
            value={tekst} onChange={(e) => setTekst(e.target.value)} onPaste={opPlakken}
            placeholder="Plak hier de mail of planning van de klant. Een screenshot kun je ook gewoon plakken (Ctrl+V)."
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
            <input ref={invoer} type="file" accept={ACCEPT} hidden
              onChange={(e) => { setBestand(e.target.files?.[0] || null); e.target.value = ''; }} />
            {bestand ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#334155' }}>
                <AttachFileIcon fontSize="small" />
                <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>{bestand.name}</Typography>
                <IconButton size="small" aria-label="Bestand weghalen" onClick={() => setBestand(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Button size="small" variant="text" startIcon={<AttachFileIcon />}
                onClick={() => invoer.current?.click()} sx={{ textTransform: 'none', color: '#475569' }}>
                Screenshot of document kiezen
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button size="small" variant="contained" disableElevation onClick={lees}
              disabled={bezig || (!tekst.trim() && !bestand)}
              startIcon={bezig ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ textTransform: 'none' }}>
              {bezig ? 'Planning lezen…' : 'Planning lezen'}
            </Button>
          </Box>
          {fout && <Alert severity="warning" sx={{ mt: 1.5 }}>{fout}</Alert>}
        </>
      )}

      {resultaat && (
        <Box>
          {resultaat.samenvatting && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {resultaat.samenvatting}
            </Typography>
          )}
          {regels.length === 0 ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Er stonden geen datums of mijlpalen in. Vul de periode hieronder zelf in.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }} data-testid="klantplanning-regels">
              {regels.map((r) => (
                <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', opacity: r.aan ? 1 : 0.55 }}>
                  <Checkbox size="small" sx={{ p: 0.5 }} checked={r.aan}
                    inputProps={{ 'aria-label': `Gebruik ${r.label || r.soort}` }}
                    onChange={(e) => wijzigRegel(r.id, 'aan', e.target.checked)} />
                  <Select size="small" value={r.soort} onChange={(e) => wijzigRegel(r.id, 'soort', e.target.value)}
                    sx={{ width: 170, fontSize: '0.8rem' }}>
                    {SOORTEN.map((s) => <MenuItem key={s.key} value={s.key} sx={{ fontSize: '0.8rem' }}>{s.label}</MenuItem>)}
                  </Select>
                  <TextField size="small" value={r.label} placeholder="Omschrijving"
                    onChange={(e) => wijzigRegel(r.id, 'label', e.target.value)}
                    sx={{ flex: 1, minWidth: 140, '& input': { fontSize: '0.8rem' } }} />
                  <TextField size="small" type="date" value={r.datum}
                    onChange={(e) => wijzigRegel(r.id, 'datum', e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ width: 150, '& input': { fontSize: '0.8rem' } }} />
                  <Typography variant="caption" sx={{ width: '100%', pl: 5, mt: -0.5, color: r.zeker ? 'text.secondary' : '#b45309' }}>
                    {r.datumTekst ? `“${r.datumTekst}”` : ''}
                    {r.deliverable ? ` · ${r.andereDeliverable ? 'andere deliverable: ' : ''}${r.deliverable}` : ''}
                    {!r.zeker && (r.datum ? ' · controleer deze datum' : ' · geen datum gevonden')}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {regels.some((r) => r.andereDeliverable) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              De planning noemt meerdere deliverables. Alleen de regels voor {deliverableNaam || 'deze deliverable'} staan aan; vink andere zelf aan als ze ook gelden.
            </Typography>
          )}
          {voorstel.waarschuwingen.map((w) => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}

          <Typography variant="body2" sx={{ mb: 1 }} data-testid="klantplanning-samenvatting">
            {wijzigingen.length
              ? <>Overnemen zet: <strong>{wijzigingen.join(' · ')}</strong></>
              : 'Er verandert niets aan de planning.'}
          </Typography>
          <FormControlLabel
            control={<Checkbox size="small" checked={bewaren} onChange={(e) => setBewaren(e.target.checked)} />}
            label={<Typography variant="caption" color="text.secondary">Klantplanning bewaren bij het project (documenten, fase Productie)</Typography>}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
            <Button size="small" onClick={() => setResultaat(null)} sx={{ textTransform: 'none', color: '#475569' }}>
              Terug
            </Button>
            <Button size="small" variant="contained" disableElevation onClick={neemOver}
              disabled={!wijzigingen.length} sx={{ textTransform: 'none' }}>
              Voorstel overnemen
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
}

export default KlantplanningImport;
